import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "../../api/http";

const INITIAL_STATE = "idle";
const VALIDATING_STATE = "validating";
const VALID_STATE = "valid";
const INVALID_STATE = "invalid";
const THROTTLED_STATE = "throttled";

function getStateCopy(state) {
	if (state === VALIDATING_STATE) {
		return {
			title: "Validando...",
			description:
				"Estamos verificando el código de forma segura. Esto tomará unos segundos.",
			style: {color: "var(--home)"},
		};
	}

	if (state === VALID_STATE) {
		return {
			title: "Certificado válido",
			description:
				"El certificado fue validado correctamente.",
			style: { color: "var(--badge-success-text)" },
		};
	}

	if (state === INVALID_STATE) {
		return {
			title: "Certificado no válido",
			description:
				"No pudimos validar el certificado con ese código.",
			style: {color: "var(--badge-neutral-text)"},
		};
	}

	if (state === THROTTLED_STATE) {
		return {
			title: "Demasiadas consultas",
			description:
				"Se alcanzó el límite de consultas. Intenta nuevamente más tarde.",
			style: {color: "var(--badge-neutral-text)"},
		};
	}

	return {
		title: "Estado inicial",
		description:
			"Ingresa el código de validación impreso en tu certificado y presiona Validar.",
		style: {color: "var(--font)"},
	};
}

export default function CertificateValidationPage() {
	const [validationCode, setValidationCode] = useState("");
	const [viewState, setViewState] = useState(INITIAL_STATE);
	const [result, setResult] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [inputError, setInputError] = useState(false);
	const timeoutRef = useRef(null);
	const requestIdRef = useRef(0);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const handleValidate = async () => {
		const code = validationCode.trim();
		if (!code) {
			setViewState(INITIAL_STATE);
			setResult(null);
			setErrorMessage("Ingresa un código de validación.");
			setInputError(true);
			return;
		}

		requestIdRef.current += 1;
		const currentRequestId = requestIdRef.current;

		setViewState(VALIDATING_STATE);
		setResult(null);
		setErrorMessage("");
		setInputError(false);

		try {
			await new Promise((resolve) => {
				timeoutRef.current = setTimeout(resolve, 5000);
			});

			if (currentRequestId !== requestIdRef.current) {
				return;
			}

			const response = await publicApi.post("/api/public/certificados/validar/", {
				validation_code: code,
			});

			if (currentRequestId !== requestIdRef.current) {
				return;
			}

			if (response.status === 200 && response.data?.valid) {
				setViewState(VALID_STATE);
				setResult(response.data.certificate || null);
				return;
			}

			setViewState(INVALID_STATE);
			setResult(null);
		} catch (error) {
			if (currentRequestId !== requestIdRef.current) {
				return;
			}

			const status = error?.response?.status;
			if (status === 429) {
				setViewState(THROTTLED_STATE);
				setErrorMessage("");
				return;
			}

			if (status === 400) {
				setViewState(INVALID_STATE);
				setErrorMessage("El código ingresado no es válido.");
				setInputError(true);
				return;
			}

			setViewState(INVALID_STATE);
			setErrorMessage("No fue posible validar el certificado en este momento.");
		} finally {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		}
	};

	const stateCopy = getStateCopy(viewState);

	return (
		<main className="page-cursos font-n">
			<div className="banner-course">
				<h4>Validar certificados</h4>
			</div>

			<section className="page-cursos-content">
				<div className="btn-back-container">
					<Link to="/">
						<button type="button">
							<i className="ri-arrow-go-back-line d-inline d-md-none"></i>
							<span className="d-none d-md-inline">Volver</span>
						</button>
					</Link>
				</div>

				<section className="admission-sec">
					<div className="admission-block">
						<h1 className="admission-title">Validar certificados</h1>
						<p className="admission-subtitle">
							Ingresa el código de validación impreso en tu certificado.
						</p>

						<form
							className="admission-form"
							onSubmit={(e) => {
								e.preventDefault();
								handleValidate();
							}}
						>
							<div className="admission-row">
								<div className="admission-col">
									<div className="admission-field">
										<label htmlFor="certificate-validation-code">
											<span>Código de validación</span>
										</label>
										<input
											id="certificate-validation-code"
											type="text"
											className="admission-input"
											style={{
												borderColor: inputError ? 'var(--red)' : undefined,
												outlineColor: inputError ? 'var(--red)' : undefined
											}}
											placeholder="Ej: 123e4567-e89b-12d3-a456-426614174000"
											value={validationCode}
											onChange={(e) => {
												setValidationCode(e.target.value);
												if (inputError) {
													setInputError(false);
													setErrorMessage("");
												}
											}}
											autoComplete="off"
											disabled={viewState === VALIDATING_STATE}
										/>
										{inputError && (
											<span className="text-danger mt-1" style={{ fontSize: '1rem', display: 'block' }}>
												{errorMessage}
											</span>
										)}
									</div>
								</div>
							</div>

							<div className="d-flex justify-content-center mt-4">
								<button
									type="submit"
									className="btn-admission"
									disabled={viewState === VALIDATING_STATE}
								>
									<span>{viewState === VALIDATING_STATE ? "Validando..." : "Validar"}</span>
								</button>
							</div>
						</form>

						<section className="card border-0 h-100 rounded-4 mt-5" style={{ background:"rgb(199 221 246 / 40%)"}}>
							<div className="card-body font-n">
								<p className={`mb-2 ${stateCopy.className || ''}`} style={{ fontWeight: 600, ...stateCopy.style}}>
									{stateCopy.title}
								</p>
								<p className="mb-0 text-muted">{stateCopy.description}</p>
								{errorMessage && !inputError ? (
									<p className="mt-3 mb-0 text-danger" style={{ fontSize: '1rem' }}>{errorMessage}</p>
								) : null}
								{viewState === VALID_STATE && result ? (
									<div className="card shadow-sm mb-3 mt-4 card-table">
										<div className="card-body">
											<p className="mb-1" style={{ color: "#1e2b64", fontWeight: 600 }}>
												Curso asociado
											</p>
											<p className="mb-1 text-muted">
												{result.course?.code ? `${result.course.code} - ` : ""}
												{result.course?.name || "Curso validado"}
											</p>
											<p className="mb-0 text-muted">
												Emitido el {result.issued_at ? new Date(result.issued_at).toLocaleDateString("es-CL") : "—"}
											</p>
										</div>
									</div>
								) : null}
							</div>
						</section>
					</div>
				</section>
			</section>
		</main>
	);
}
